<template>
  <AppDialog
    :show="show"
    :title="title"
    append-to-body
    class="paid-addon-cancelation cancellation-feedback-dialog"
    @close="toggleDialog"
  >
    <div
      v-if="loading"
      class="spinner d-flex align-items-center justify-content-center p-20"
    >
      <div class="spinner-wrapper absolute">
        <span class="hi hi-spinner hi-32 text-blue" />
      </div>
    </div>
    <ValidationObserver ref="dialogObserver">
      <h5 class="info mb-30 mt-0">We value your feedback. Please tell us why you are cancelling.</h5>
      <ValidationProvider
        v-slot="{ errors }"
        rules="required"
        name="reason"
      >
        <div
          class="form-group reason-input"
          :class="{ 'has-error': !!errors?.length }"
        >
          <label
            for="reason"
            class="control-label"
          >
            Cancellation reason
          </label>
          <AppSelect
            v-model="form.reason"
            :options="cancellationReasons"
            :select2-options="selectOptions"
            :disabled="loading"
          />
          <span class="help-block">{{ errors[0] }}</span>
        </div>
      </ValidationProvider>
      <ValidationProvider
        v-slot="{ errors }"
        name="otherReason"
      >
        <div
          v-if="showOtherReason"
          class="form-group other-input mt-10"
          :class="{ 'has-error': !!errors?.length }"
        >
          <textarea
            id="details"
            v-model="form.other"
            class="form-control text"
            rows="4"
            placeholder="Add a custom reason"
          />
          <span
            v-if="errors"
            class="help-block"
          >
            {{ errors[0] }}
          </span>
        </div>
      </ValidationProvider>
      <ValidationProvider
        v-slot="{ errors }"
        name="details"
      >
        <div
          class="form-group details-input mt-30"
          :class="{ 'has-error': !!errors?.length }"
        >
          <label
            for="details"
            class="control-label"
          >
            Details
          </label>
          <textarea
            id="details"
            v-model="form.details"
            class="form-control text"
            rows="4"
            placeholder="Please tell us why you're leaving. We appreciate your feedback."
          />
          <span class="help-block">{{ errors[0] }}</span>
        </div>
      </ValidationProvider>
    </ValidationObserver>
    <div class="modal-footer d-flex justify-content-end pt-30 mt-auto flex-wrap p-0">
      <a
        v-if="eligible"
        class="btn btn-default"
        @click="previous"
      >
        Back to one-time offer
      </a>
      <a
        class="btn btn-danger"
        @click="submit"
      >
        Remove Insights
      </a>
    </div>
  </AppDialog>
</template>

<script>
import { InsightsResource, PaidAddonsResource } from '~/resources';

export default {
  name: 'CancellationFeedbackDialog',
  props: {
    id: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    show: {
      type: Boolean,
      default: false,
    },
    organizationId: {
      type: Number,
      required: true,
    },
    eligible: {
      type: Boolean,
      required: true,
    },
  },
  data: () => ({
    loading: false,
    cancellationReasons: [],
    selectOptions: {
      placeholder: 'Select a reason',
      width: '100%',
    },
    form: {
      reason: null,
      details: null,
      other: null,
    },
  }),
  computed: {
    showOtherReason() {
      return this.form.reason === 'Other';
    },
    formValues() {
      return Object.fromEntries(Object.entries(this.form).filter(([key, value]) => value !== null));
    },
  },
  mounted() {
    this.fetchCancellationReasons();
  },
  methods: {
    previous(event) {
      this.$emit('previous', this.id, event);
    },
    toggleDialog(event) {
      this.$emit('toggleDialog', 'feedback', false, event);
    },
    sendAnalytics() {
      const traits = window.analytics?.user?.()?.traits?.() || {};
      const { reason, other } = this.formValues;
      const finalReason = reason === 'Other' ? other : reason;

      window.analytics?.track('Insights cancellation reason', {
        reason: finalReason || '',
      }, {
        context: { traits }
      });
    },
    async fetchCancellationReasons() {
      try {
        this.loading = true;
        const params = { organizationId: this.organizationId };
        const response = await InsightsResource.cancellationReasons(params);

        if (response && response.reasons) {
          this.cancellationReasons = response.reasons;
          this.loading = false;
        } else {
          throw new Error('Unable to fetch data for Insights usage.');
        }
      } catch (error) {
        this.$appMessage('Unable to fetch data for the Insights usage. Try again later.');
      }
    },
    async submit() {
      const isValid = await this.$refs.dialogObserver.validate();

      if (isValid) {
        this.disableAddon().then(async () => {
          await this.saveCancellationForm();
        });
      }
    },
    async disableAddon() {
      try {
        const params = { organizationId: this.organizationId, addonSym: 'insights' };
        const response = await PaidAddonsResource.disableAddon(params);

        if (response && response.reload) {
          return true;
        }

        if (response && response.errors) {
          throw new Error(response.errors[0] || 'Unable to complete your request.');
        }
      } catch (error) {
        this.$appMessage(error || 'Unable to complete your request. Try again later.');
      }
    },
    async saveCancellationForm() {
      try {
        const params = { organizationId: this.organizationId };
        const response = await InsightsResource.saveCancellationForm(params, this.formValues);

        if (response && response.success) {
          this.sendAnalytics();
          window.location.reload();
        } else if (response && response.errors) {
          throw new Error(response.errors[0] || 'Unable to complete your request.');
        }
      } catch (error) {
        this.$appMessage(error || 'Unable to complete your request. Try again later.');
      }
    },
  },
};
</script>
